import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { Data, Layout, LayoutAxis } from "plotly.js";
import _ from "lodash";

import { VideoAnalysisService } from "../../../../services/inspection/VideoAnalysisService";
import { videoPlayerService } from "../../../../services/video-player/VideoPlayerService";
import { secondsToMinutesAndSeconds } from "../../../../helpers/MathHelpers";
import { globalModule } from "../../../../services/service-provider/GlobalModule";
import { translationService } from "../../../../services/translation/TranslationService";
import {
  getProgressLine,
  getHoveredLine,
  getProgressOrHoveredAnnotation,
  getPlotDataForAddedDamages,
  getPlotDataForPauses,
  getTimestamps,
  getPlotDataForDetectedDamages,
  getBackgroundForDetectedDamages,
  getLayoutConfiguration,
  getOverallConfidenceLine,
} from "./PlotHelper";
import { DamagesStorage } from "../../../../services/damage/DamagesStorage";
import { ConfidenceOptions } from "./tools/Tools";

export interface PlotProps {
  videoAnalysisService: VideoAnalysisService;
  damagesStorage: DamagesStorage;
  onDoubleClick: (xAxis:number) => void;
}

export default observer(function OverviewPlot(props: PlotProps) {
  const { videoAnalysisService, damagesStorage, onDoubleClick } = props;
  const [mousePosition, setMousePosition] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });

  useEffect(() => {
    const plotDiv = document.getElementById("overviewPlot") as any;

    plotDiv?.addEventListener("click", (e: any) => {
      if (!e.target.getAttribute("class")?.includes("nsewdrag drag")) return;

      const xPos = plotDiv._fullLayout.xaxis.p2d(e.clientX - e.target.getBoundingClientRect().left);
      videoPlayerService.jumpTo(_.clamp(xPos, 0, videoPlayerService.videoDuration));
    });

    return () => {
      plotDiv?.removeEventListener("click", () => {});
    };
  }, []);

  useEffect(() => {
    const plotDiv = document.getElementById("overviewPlot") as any;

    plotDiv?.addEventListener("mousemove", (e: any) => {
      if (!e.target.getAttribute("class")?.includes("nsewdrag drag")) return;

      const xPos = plotDiv._fullLayout.xaxis.p2d(e.clientX - e.target.getBoundingClientRect().left);
      const yPos = plotDiv._fullLayout.yaxis.p2d(e.clientY - e.target.getBoundingClientRect().top);
      setMousePosition({ x: xPos, y: yPos });
    });
    plotDiv?.addEventListener("mouseleave", (e: any) => {
      const target = document.elementFromPoint(e.clientX, e.clientY);
      if (target?.getAttribute("class")?.includes("dragcover")) return;

      setMousePosition({ x: null, y: null });
    });

    return () => {
      plotDiv?.removeEventListener("mousemove", () => {});
      plotDiv?.removeEventListener("mouseleave", () => {});
    };
  }, []);

  const createPlotData = useCallback(() => {
    let plotsData: Data[] = [];

    plotsData.push(getPlotDataForPauses(videoAnalysisService, videoPlayerService));

    videoAnalysisService.detectedMainCodes.forEach((detectedMainCode) => {
      if (detectedMainCode.isVisible) {
        plotsData.push(
          getPlotDataForDetectedDamages(videoAnalysisService, videoPlayerService, detectedMainCode)
        );
      }
    });

    plotsData.push(
      getPlotDataForAddedDamages(damagesStorage.damageDetailsForDiagram, videoAnalysisService, "y2")
    );

    return plotsData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    damagesStorage.damageDetailsForDiagram,
    videoAnalysisService,
    videoAnalysisService.analysisOverallConfidence,
    videoAnalysisService.analysisConfidenceOption,
    videoAnalysisService.analysisIndividualConfidenceLevels,
  ]);

  const xAxisTimestampValues = useMemo(() => {
    return getTimestamps(videoPlayerService.videoDuration, 5, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlayerService.videoDuration]);

  const xaxis = useMemo<Partial<LayoutAxis>>(
    () => ({
      anchor: "y2",
      showline: false,
      showticklabels: true,
      tickvals: xAxisTimestampValues,
      ticktext: xAxisTimestampValues.map((t) => secondsToMinutesAndSeconds(t)),
      autorange: false,
      range: [0, videoPlayerService.videoDuration],
      zeroline: false,
      showgrid: false,
    }),
    [xAxisTimestampValues]
  );

  const layout = useMemo(() => {
    const layout = {} as Layout;
    layout.margin = { l: 23, r: 20, b: 28, t: 30, pad: 0 };
    layout.showlegend = false;
    layout.shapes = [];
    layout.annotations = [];
    layout.xaxis = xaxis;
    layout.autosize = true;

    layout.yaxis = {
      ticks: "outside",
      tickwidth: 2,
      tickvals: [0, 1],
      range: [0, 1],
      domain: [0.07, 1],
      zeroline: true,
      showgrid: false,
      fixedrange: true,
      title: translationService.translate("Analysis.overview-plot.yaxis.legend"),
    };

    layout.yaxis2 = {
      range: [0, 1],
      domain: [0, 0.06],
      showticklabels: false,
      showgrid: false,
    };

    layout.shapes.push(getProgressLine(videoPlayerService.videoProgress));

    if (videoAnalysisService.analysisConfidenceOption === ConfidenceOptions.Overall) {
      layout.shapes.push(getOverallConfidenceLine(videoAnalysisService));
    }

    const hoveredLine = getHoveredLine(videoPlayerService.videoDuration, mousePosition.x);
    if (hoveredLine) layout.shapes.push(hoveredLine);
    layout.shapes.push(getBackgroundForDetectedDamages(videoPlayerService));

    layout.annotations.push(
      ...getProgressOrHoveredAnnotation(
        videoPlayerService.videoProgress,
        videoPlayerService.videoDuration,
        videoAnalysisService,
        damagesStorage.damageDetailsForDiagram,
        mousePosition.x
      )
    );

    videoAnalysisService.plotRelayoutDone();

    return layout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    videoPlayerService.videoProgress,
    videoPlayerService.videoDuration,
    translationService.language,
    mousePosition,
    videoAnalysisService.plotNeedsRelayout,
    damagesStorage.isCollapsed,
    videoAnalysisService.analysisOverallConfidence,
    videoAnalysisService.analysisConfidenceOption,
  ]);

  return (
    <Plot
      divId="overviewPlot"
      data={createPlotData()}
      style={{
        width: "100%",
        height: "100%",
      }}
      layout={layout}
      config={getLayoutConfiguration()}
      onDoubleClick={() => onDoubleClick(mousePosition.x!)}
    />
  );
});
